import { inject, signal, WritableSignal } from '@angular/core';
import { CustomCombinedIdTagTypeEnum } from 'src/app/shared/enums/custom-combined-id-tag-type.enum';
import { IdTag, IdTagSearchEvent } from '../id-tag-model';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  skip,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { IdTagTypeEnum } from 'src/app/shared/enums/id-tag-type.enum';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { IdTagService } from 'src/app/shared/services/id-tag.service';
import { ChargingUser } from '../../charging-users/charging-user.model';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { BaseListStateService, IFilterControls } from 'src/app/shared/models/base.state.service';
import { FormControl, FormGroup } from '@angular/forms';
import { PagedResult } from 'src/app/shared/models/paging';

export enum IdTagListQueryParams {
  chargingUserId = 'chargingUserId',
  organizationId = 'organizationId',
  departmentId = 'departmentId',
  query = 'query',
  idTagStatus = 'idTagStatus',
  customCombinedIdTagType = 'customCombinedIdTagType',
  includeInactive = 'includeInactive',
  printedId = 'printedId',
}
interface FilterState {
  organizationId: number | null;
  departmentId: number | null;
  query: string | null;
  idTagStatus: IdTagStatusEnum | null;
  customCombinedIdTagType: CustomCombinedIdTagTypeEnum | null;
  chargingUserId: number | null;
  includeInactive: boolean;
  printedId: number | null;
}
export class IdTagListStateService extends BaseListStateService<IdTag, IdTagListQueryParams, FilterState> {
  private chargingUserService = inject(ChargingUserService);
  private idTagService = inject(IdTagService);
  public refreshIdTagSubject = new Subject<IdTag & { originalId?: number }>();
  public resetDepartmentFilterSignal = signal<{ reset: boolean }>({ reset: false });
  public resetOrgFilterSignal = signal<{ reset: boolean }>({ reset: false });
  protected createQueryParams(filters: Partial<FilterState>): Record<string, string | null> {
    const queryParams: Record<IdTagListQueryParams, string | null> = {
      [IdTagListQueryParams.chargingUserId]: filters.chargingUserId ? filters.chargingUserId.toString() : null,
      [IdTagListQueryParams.organizationId]: filters.organizationId ? filters.organizationId.toString() : null,
      [IdTagListQueryParams.departmentId]: filters.departmentId ? filters.departmentId.toString() : null,
      [IdTagListQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
      [IdTagListQueryParams.idTagStatus]: filters.idTagStatus ? filters.idTagStatus.toString() : null,
      [IdTagListQueryParams.customCombinedIdTagType]: filters.customCombinedIdTagType ? filters.customCombinedIdTagType.toString() : null,
      [IdTagListQueryParams.includeInactive]: filters.includeInactive ? filters.includeInactive.toString() : null,
      [IdTagListQueryParams.printedId]: filters.printedId ? filters.printedId.toString() : null,
    };
    return queryParams;
  }

  public statuses: IdTagStatusEnum[] = [
    IdTagStatusEnum.Pending,
    IdTagStatusEnum.Active,
    IdTagStatusEnum.Inactive,
    IdTagStatusEnum.NoIdTagGroup,
    IdTagStatusEnum.Blocked,
  ];

  public statusesFiltered: IdTagStatusEnum[] = Object.assign([], this.statuses);
  focusedIdTag: WritableSignal<IdTag | null> = signal(null);
  form = new FormGroup<IFilterControls<FilterState>>({
    organizationId: new FormControl<number | null>(
      (() => {
        const param = this.queryParamMap$()?.get(IdTagListQueryParams.organizationId) ?? null;
        return param ? parseInt(param) : null;
      })()
    ),
    departmentId: new FormControl<number | null>(
      (() => {
        const param = this.queryParamMap$()?.get(IdTagListQueryParams.departmentId) ?? null;
        return param ? parseInt(param) : null;
      })()
    ),
    query: new FormControl<string | null>(
      (() => {
        const param = this.queryParamMap$()?.get(IdTagListQueryParams.query) ?? null;
        return param ? decodeURIComponent(param) : null;
      })()
    ),
    customCombinedIdTagType: new FormControl<CustomCombinedIdTagTypeEnum | null>(
      (() => {
        const param = this.queryParamMap$()?.get(IdTagListQueryParams.customCombinedIdTagType);
        return param ? this.filteringService.safeCastEnum(CustomCombinedIdTagTypeEnum, param) : null;
      })()
    ),
    idTagStatus: new FormControl<IdTagStatusEnum | null>(
      (() => {
        const param = this.queryParamMap$()?.get(IdTagListQueryParams.idTagStatus);
        return param ? this.filteringService.safeCastEnum(IdTagStatusEnum, param) : null;
      })()
    ),
    chargingUserId: new FormControl<number | null>(
      (() => {
        const param = this.queryParamMap$()?.get(IdTagListQueryParams.chargingUserId) ?? null;
        return param ? parseInt(param) : null;
      })()
    ),
    includeInactive: new FormControl<boolean>(
      (() => {
        const param = this.queryParamMap$()?.get(IdTagListQueryParams.includeInactive) ?? null;
        return param ? decodeURIComponent(param) === 'true' : false;
      })(),
      { nonNullable: true }
    ),
    printedId: new FormControl<number | null>(
      (() => {
        const param = this.queryParamMap$()?.get(IdTagListQueryParams.printedId) ?? null;
        return param ? parseInt(param) : null;
      })()
    ),
  });

  idTags = signal<PagedResult<IdTag>>({
    items: [],
    totalCount: 0,
    pageIndex: 0,
    pageSize: this.defaultPageSize,
  });

  constructor() {
    super();
    this.resetPageOnFilterChange();

    // Watch for query param changes and sync with form (for navigation from quick search while already on the page)
    toObservable(this.queryParamMap$)
      .pipe(
        skip(1), // Skip the initial value since form is already initialized with it
        takeUntilDestroyed()
      )
      .subscribe(queryParamMap => {
        if (!queryParamMap) return;

        const newPrintedId = queryParamMap.get(IdTagListQueryParams.printedId);
        const newIncludeInactive = queryParamMap.get(IdTagListQueryParams.includeInactive);
        const currentPrintedId = this.form.get('printedId')?.value;
        const parsedNewPrintedId = newPrintedId ? parseInt(newPrintedId) : null;

        // Only update if the printedId from URL differs from current form value
        if (parsedNewPrintedId !== currentPrintedId) {
          this.form.patchValue(
            {
              printedId: parsedNewPrintedId,
              includeInactive: newIncludeInactive === 'true',
            },
            { emitEvent: true }
          );
        }
      });
  }

  refreshIdTag(idTag: IdTag, originalId = idTag.id) {
    this.refreshIdTagSubject.next({ ...idTag, originalId });
  }

  _ = this.refreshIdTagSubject
    .pipe(
      switchMap(idTag =>
        this.idTagService.getIdTag(idTag.id).pipe(
          tap(freshIdTag => {
            const originalId = idTag.originalId ?? idTag.id;
            const itemIndex = this.idTags().items.findIndex(item => item.id === originalId);
            if (itemIndex === -1) return;
            const highlight = new BehaviorSubject<boolean>(true);
            this.idTags.update(current => {
              const newItems = [...current.items];
              //replace item in list with new one
              newItems[itemIndex] = {
                ...freshIdTag,
                highlight: highlight,
              };
              return {
                ...current,
                items: newItems,
              };
            });
            setTimeout(() => {
              highlight.next(false);
              this.idTags.update(current => ({
                ...current,
                items: current.items.map(item => (item.highlight === highlight ? { ...item } : item)),
              }));
            }, 3000);
          })
        )
      ),
      takeUntilDestroyed()
    )
    .subscribe();

  focusedUser$: Observable<ChargingUser | null> = this.form.valueChanges.pipe(
    map(filters => filters.chargingUserId),
    distinctUntilChanged(),
    switchMap(chargingUserId => {
      if (chargingUserId) {
        return this.chargingUserService.getChargingUser(chargingUserId);
      }
      return of(null);
    }),
    takeUntilDestroyed()
  );

  private idTags$ = combineLatest([
    this.form.valueChanges.pipe(startWith(this.form.value)),
    toObservable(this.refreshTrigger),
    toObservable(this.viewState),
  ])
    .pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.loading.set(true)),
      tap(([form, viewstate]) => {
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: this.createQueryParams(form),
          queryParamsHandling: 'merge',
        });
      }),
      tap(() => this.loading.set(true)),
      switchMap(([form, refresh, viewstate]) => {
        if (form.includeInactive) {
          this.statusesFiltered = Object.assign([], this.statuses);
        }

        // When printedId is set, search only by printedId (ignoring other filters)
        if (form.printedId) {
          const searchEvent = <IdTagSearchEvent>{
            printedId: form.printedId,
            includeInactive: form.includeInactive,
            paging: { length: 0, pageIndex: 0, pageSize: 1000 },
          };
          return this.idTagService.getIdTags(searchEvent).pipe(map(result => ({ result, form })));
        }

        const searchEvent = <IdTagSearchEvent>{
          departmentId: form.departmentId,
          organizationId: form.organizationId,
          query: form.query,
          status: form.idTagStatus,
          printedId: form.printedId,
          idTagType: this.customCombinedIdTagTypeToIdTagType(form.customCombinedIdTagType),
          idTagUserType: this.customCombinedIdTagTypeToIdTagUserType(form.customCombinedIdTagType),
          chargingUserId: form.chargingUserId,
          sorting: viewstate.sort,
          includeInactive: form.includeInactive,
          paging: viewstate.page,
        };
        this.chageFilterStatuses(form);

        return this.idTagService.getIdTags(searchEvent).pipe(map(result => ({ result, form })));
      }),
      tap(({ result, form }) => {
        if (form.printedId && result.items.length >= 1) {
          this.focusedIdTag.set(result.items[0]);
        } else if (!form.printedId) {
          this.focusedIdTag.set(null);
        }
        this.idTags.set(result);
        this.loading.set(false);
      }),
      takeUntilDestroyed()
    )
    .subscribe();

  chageFilterStatuses(filters: Partial<FilterState>) {
    if (filters.includeInactive) {
      this.statusesFiltered = Object.assign([], this.statuses);
    } else {
      this.statusesFiltered = this.statuses.filter(
        status => status !== IdTagStatusEnum.Inactive && status !== IdTagStatusEnum.NoIdTagGroup && status != IdTagStatusEnum.Blocked
      );
    }
  }

  private customCombinedIdTagTypeToIdTagType(customCombinedIdTagTypeEnum: CustomCombinedIdTagTypeEnum | null | undefined) {
    switch (customCombinedIdTagTypeEnum) {
      case CustomCombinedIdTagTypeEnum.FleetIdTag:
        return IdTagTypeEnum.FleetIdTag;
      case CustomCombinedIdTagTypeEnum.ChargingUserIdTag:
        return IdTagTypeEnum.ChargingUserIdTag;
      default:
        return null;
    }
  }

  private customCombinedIdTagTypeToIdTagUserType(customCombinedIdTagTypeEnum: CustomCombinedIdTagTypeEnum | null | undefined) {
    switch (customCombinedIdTagTypeEnum) {
      case CustomCombinedIdTagTypeEnum.CompanyCar:
        return IdTagUserType.CompanyCar;
      case CustomCombinedIdTagTypeEnum.NoCompanyCar:
        return IdTagUserType.NoCompanyCar;
      default:
        return null;
    }
  }

  public resetFilter() {
    this.focusedIdTag.set(null);
    this.form.patchValue({
      organizationId: null,
      departmentId: null,
      query: null,
      idTagStatus: null,
      printedId: null,
      customCombinedIdTagType: null,
      chargingUserId: null,
      includeInactive: false,
    });
    this.viewState.update(current => ({
      ...current,
      sort: this.defaultSort,
      page: { length: 0, pageIndex: 0, pageSize: this.defaultPageSize },
    }));

    this.resetOrgFilterSignal.set({ reset: true });
    this.resetDepartmentFilterSignal.set({ reset: true });
  }
}
